import { useAuth } from '@core/context/AuthContext';
import { useCategories } from '@core/hooks/useCategories';
import { useCoordinates } from '@core/hooks/useCoordinates';
import { useInventory } from '@core/hooks/useInventory';
import { useInventoryState } from '@core/context/InventoryContext';
import { useLocations } from '@core/hooks/useLocations';
import { useStorage } from '@core/hooks/useStorage';
import { Drawer } from '@core/ui';
import { Settings, Save, Plus, Minus, PackagePlus, Edit2, AlertCircle, Database, Tag, MapPin, Layers } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import BarcodeScanner from './BarcodeScanner';
import CategoryManager from './CategoryManager';

// Sub-components
import MovementPanel from './inventory/MovementPanel';
import ProductForm from './inventory/ProductForm';
import SettingsPanel from './inventory/SettingsPanel';

const drawerReducer = (state, action) => {
    switch (action.type) {
        case 'RESET':
            return {
                ...state,
                amount: 1,
                note: '',
                isSubmitting: false,
                imageFile: null,
                imagePreview: null,
                skuTouched: false,   // ← reset validation flag on every open/close
                ...action.payload
            };
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_FORM_DATA':
            return {
                ...state,
                formData: { ...state.formData, ...action.value },
                // Auto-mark touched when user explicitly changes the SKU field
                skuTouched: 'sku' in action.value ? true : state.skuTouched,
            };
        case 'SET_LOCATION':
            return {
                ...state,
                formData: {
                    ...state.formData,
                    location: { ...state.formData.location, ...action.value }
                }
            };
        case 'START_SUBMIT':
            return { ...state, isSubmitting: true, skuTouched: false }; // ← desactiva validación al guardar
        case 'END_SUBMIT':
            return { ...state, isSubmitting: false };
        default:
            return state;
    }
};

const EMPTY_FORM = {
    sku: '',
    name: '',
    description: '',
    category: '',
    categoryId: '',
    location: { section: '', sectionId: '', aisle: '', aisleId: '', rack: '', rackId: '', container: '' },
    unit: 'units',
    stock_current: 0,
    stock_min: 0,
    imageUrl: ''
};

/**
 * InventoryDrawer - Master Warehouse Logic
 * handles IN/OUT movements, Settings, and ADD new item.
 */
const InventoryDrawer = ({
    item, type, isOpen, onClose, registerMovement, updateThresholds, addProduct, updateProduct, toggleProductStatus
}) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { products } = useInventoryState();
    const { categories, loading: categoriesLoading } = useCategories();
    const { locations, loading: locationsLoading } = useLocations();
    const { aisles, racks, loading: coordsLoading } = useCoordinates();
    const { uploadImage } = useStorage();

    const [state, dispatch] = React.useReducer(drawerReducer, {
        isScannerOpen: false,
        isCatManagerOpen: false,
        imageFile: null,
        imagePreview: null,
        amount: 1,
        note: '',
        min: 0,
        formData: EMPTY_FORM,
        isSubmitting: false,
        skuTouched: false,
    });

    const {
        isScannerOpen, isCatManagerOpen, imageFile, imagePreview,
        amount, note, min, max, formData, isSubmitting, skuTouched
    } = state;

    // -- FILTERS & COMPUTATIONS --
    const filteredAisles = useMemo(() => {
        const zoneName = formData.location.section;
        const target = locations.find(loc => loc.name === zoneName);
        return target
            ? aisles.filter(a => a.locationId === target.id && (a.isActive !== false || a.name === formData.location?.aisle))
            : [];
    }, [formData.location.section, formData.location.aisle, locations, aisles]);

    const filteredRacks = useMemo(() => {
        const zoneName = formData.location.section;
        const aisleName = formData.location.aisle;
        const zone = locations.find(loc => loc.name === zoneName);
        const aisle = aisles.find(a => a.name === aisleName && a.locationId === zone?.id);
        return aisle
            ? racks.filter(r => r.aisleId === aisle.id && (r.isActive !== false || r.name === formData.location?.rack))
            : [];
    }, [formData.location.section, formData.location.aisle, formData.location.rack, locations, aisles, racks]);

    // Real-time SKU duplicate check — only active once the user has typed in the SKU field
    const skuConflict = useMemo(() => {
        if (type !== 'ADD') return false;
        if (!skuTouched) return false;           // ← no flash on open or after save
        const typed = String(formData.sku || '').trim().toUpperCase();
        if (!typed) return false;
        return products.some(p => String(p.sku || '').trim().toUpperCase() === typed);
    }, [type, formData.sku, products, skuTouched]);

    useEffect(() => {
        if (!isOpen) return;
        if (item && (type === 'SETTINGS' || type === 'IN' || type === 'OUT')) {
            dispatch({ type: 'RESET', payload: { min: item.stock_min || 0, formData: EMPTY_FORM } });
        } else if (type === 'EDIT' && item) {
            // Migration: if name is empty but description has a value, treat description as name
            const migratedName = item.name || item.description || '';
            const migratedDescription = item.name ? (item.description || '') : '';
            dispatch({ type: 'RESET', payload: { formData: { ...item, name: migratedName, description: migratedDescription, location: item.location || { section: '', container: '' } }, imagePreview: item.imageUrl || null } });
        } else if (type === 'ADD') {
            dispatch({
                type: 'RESET', payload: {
                    formData: {
                        ...EMPTY_FORM,
                        category: item?.type === 'category' ? item.name : '',
                        categoryId: item?.type === 'category' ? item.id : '',
                        location: { ...EMPTY_FORM.location, section: item?.type === 'location' ? item.name : '', sectionId: item?.type === 'location' ? item.id : '' }
                    }
                }
            });
        }
    }, [item, type, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (type === 'ADD') {
            if (!formData.location?.section) {
                toast.error('Location Zone is required');
                return;
            }
            if (!formData.location?.aisle) {
                toast.error('Shelf / Cabinet is required');
                return;
            }
            if (!formData.location?.rack) {
                toast.error('Floor / Level is required');
                return;
            }
            if (!formData.location?.container || !formData.location.container.trim()) {
                toast.error('Bin / Detail is required');
                return;
            }
            const stockVal = Number(formData.stock_current);
            if (formData.stock_current === undefined || formData.stock_current === null || formData.stock_current === '' || Number.isNaN(stockVal)) {
                toast.error('Initial Inventory is required');
                return;
            }
            if (stockVal < 0) {
                toast.error('Initial Inventory must be greater than or equal to zero');
                return;
            }
        }

        dispatch({ type: 'START_SUBMIT' });
        try {
            let finalImageUrl = formData.imageUrl;
            if (imageFile) {
                const storagePath = `products/${formData.sku || Date.now()}_${imageFile.name}`;
                finalImageUrl = await uploadImage(imageFile, storagePath);
            }
            const submissionData = { ...formData, imageUrl: finalImageUrl };

            if (type === 'SETTINGS') await updateThresholds(item.id, min);
            else if (type === 'ADD') await addProduct(submissionData);
            else if (type === 'EDIT') await updateProduct(item.id, submissionData);
            else await registerMovement(item, type === 'OUT' ? -Math.abs(amount) : Math.abs(amount), type, note);

            toast.success('Sync complete');
            onClose();
        } catch (err) { toast.error(err.message || 'Operation failed'); }
        finally { dispatch({ type: 'END_SUBMIT' }); }
    };

    const config = useMemo(() => {
        const MAP = {
            IN: { title: 'Material Entry', sub: 'Add units', icon: Plus, cl: 'bg-green-600 shadow-green-500/20' },
            OUT: { title: 'Material Exit', sub: 'Remove units', icon: Minus, cl: 'bg-red-600 shadow-red-500/20' },
            SETTINGS: { title: 'Adjust Thresholds', sub: 'Configure health', icon: Settings, cl: 'bg-primary shadow-primary/20' },
            ADD: { title: 'New Item', sub: 'Add to catalog', icon: PackagePlus, cl: 'bg-accent shadow-accent/20' },
            EDIT: { title: 'Edit Item', sub: 'Modify tech info', icon: Edit2, cl: 'bg-primary shadow-primary/20' }
        };
        return MAP[type] || {};
    }, [type]);

    const isReadOnly = (type === 'SETTINGS' || type === 'ADD' || type === 'EDIT') && user?.role !== 'ADMIN';

    return (
        <Drawer isOpen={isOpen} onClose={onClose} title={config.title} subtitle={config.sub} icon={config.icon} width={(type === 'ADD' || type === 'EDIT') ? 'max-w-xl' : 'max-w-md'}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                {isReadOnly && (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl text-amber-700">
                        <AlertCircle size={20} className="shrink-0" />
                        <span className="text-xs font-bold leading-tight">READ-ONLY: Admin required.</span>
                    </div>
                )}

                {(type === 'IN' || type === 'OUT' || type === 'SETTINGS') && item && (
                    <div className="flex items-start gap-4 p-5 bg-gray-50/50 rounded-3xl border border-gray-100 italic">
                        <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm text-2xl font-black text-primary">
                            {React.createElement(config.icon, { size: 28 })}
                        </div>
                        <div className="flex flex-col gap-1">
                            <h4 className="font-black text-primary text-lg leading-tight">{item.name || item.description || 'N/A'}</h4>
                            {item.name && item.description && item.description !== item.name && (
                                <span className="text-[11px] font-medium text-slate-400 italic line-clamp-2 leading-tight">
                                    {item.description}
                                </span>
                            )}
                            <span className="text-[10px] font-black text-gray-400 capitalize bg-white px-2 py-0.5 rounded-lg border border-gray-100 w-fit">SKU: {item.sku || item.id}</span>
                        </div>
                    </div>
                )}

                {type === 'EDIT' && item?.isActive === false && (
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                        <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-[10px] font-bold text-amber-700 leading-relaxed">
                            This item is deactivated and will not appear in the self-service terminal. Activate it to make it visible again.
                        </p>
                    </div>
                )}
                {type === 'SETTINGS' && <SettingsPanel min={min} dispatch={dispatch} />}
                {(type === 'ADD' || type === 'EDIT') && (
                    <ProductForm
                        formData={formData} dispatch={dispatch} categories={categories} locations={locations}
                        filteredAisles={filteredAisles} filteredRacks={filteredRacks} type={type} item={item}
                        categoriesLoading={categoriesLoading} locationsLoading={locationsLoading} coordsLoading={coordsLoading}
                        onManageCatalog={() => { onClose(); navigate('/catalogos'); }}
                        skuConflict={skuConflict}
                    />
                )}
                {(type === 'IN' || type === 'OUT') && <MovementPanel amount={amount} note={note} dispatch={dispatch} />}

                <CategoryManager isOpen={isCatManagerOpen} onClose={() => dispatch({ type: 'SET_FIELD', field: 'isCatManagerOpen', value: false })} />

                <div className="flex items-center gap-4 mt-auto pt-6 sticky bottom-0 bg-white/90 backdrop-blur-md pb-2">
                    {type === 'EDIT' && (
                        <button
                            type="button"
                            onClick={async () => {
                                await toggleProductStatus(item.id, item.isActive !== false);
                                onClose();
                            }}
                            className={`flex-[1.5] py-5 text-white font-black rounded-2xl shadow-xl transition-all text-xs uppercase tracking-widest ${item.isActive !== false ? 'bg-red-500 hover:bg-red-600 shadow-red-500/20' : 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20'}`}
                        >
                            {item.isActive !== false ? 'Deactivate' : 'Activate'}
                        </button>
                    )}
                    <button type="button" onClick={onClose} className="flex-1 py-5 bg-gray-100 text-gray-400 font-black rounded-2xl hover:bg-gray-200 transition-all text-xs uppercase italic">Cancel</button>
                    <button type="submit" disabled={isSubmitting || skuConflict} className={`flex-[2] py-5 text-white font-black rounded-2xl shadow-2xl transition-all flex items-center justify-center gap-3 ${config.cl} disabled:opacity-50 uppercase text-xs tracking-widest`}>
                        {isSubmitting ? 'Processing...' : <><Save size={18} /> SAVE</>}
                    </button>
                </div>
            </form>

            {isScannerOpen && <BarcodeScanner onScan={(code) => dispatch({ type: 'SET_FORM_DATA', value: { sku: code.toUpperCase() } })} onClose={() => dispatch({ type: 'SET_FIELD', field: 'isScannerOpen', value: false })} />}
        </Drawer>
    );
};

export default InventoryDrawer;
